// utils/queryGeneration.ts
import { Change } from "@adornis/base/change.js";
import {
  Kind
} from "graphql";
import { getTypeString, isGQLScalarType } from "../baseqlTypes.js";
import "../entities/adornisEntity.js";
import { baseqlMetaData } from "../metadata/metaDataStore.js";
import { BaseQLUnion } from "../metadata/union.js";
var generateOperationString = (opType, op, gqlFields) => {
  if (typeof gqlFields === "string")
    throw new Error(
      `generateOperationString: Excepected BaseQLSelectionSet as gqlFields parameter but got string. Operation name: ${op.name}`
    );
  const selection = gqlFields ? buildGQLSelectionSet(op.type, gqlFields) : { fragments: {}, selection: "" };
  const finalSelection = selection.selection ? `{${selection.selection} }` : "";
  const fragments = Object.values(selection.fragments).map((fragment) => fragment.fragment).join("\n");
  const finalFragments = fragments ? `
${fragments}` : "";
  return `${opType}${variableDefinition(op.params)} {
  ${op.name}${operationParameters(op.params)}${directives(op)} ${finalSelection}
} ${finalFragments}`;
};
var variableDefinition = (params) => params.length ? "(" + params.reduce((acc, param, index) => {
  const paramNode = baseqlMetaData.entities.find((n) => n.type() === param.type());
  return acc + (index === 0 ? "" : ", ") + "$" + param.name + ": " + "[".repeat(param.arrayDepth()) + getTypeString(param.type, true) + (paramNode?.children?.length ? "Union" : "") + "]".repeat(param.arrayDepth());
}, "") + ")" : "";
var operationParameters = (params) => params.length ? "(" + params.reduce((acc, param, index) => acc + (index === 0 ? "" : ", ") + param.name + ": $" + param.name, "") + ")" : "";
var directives = (op) => "";
var narrow = (entity) => {
  return (selSet) => {
    return entity;
  };
};
function selectionSet(op, depth, blacklist) {
  return allFieldsSelectionSetObject(op, depth, blacklist);
}
var allFieldsSelectionSetObject = (op, depth, blacklist = [], childClass = false) => {
  if (!depth)
    return 1;
  if (isGQLScalarType(typeof op === "function" ? op : op.type))
    return 1;
  const opResolved = typeof op === "function" ? op() : op;
  if (opResolved instanceof BaseQLUnion)
    return baseqlMetaData.unions[opResolved._class].types().reduce((acc, t) => {
      const selection = allFieldsSelectionSetObject(() => t, depth, blacklist);
      if (typeof selection === "number")
        throw new Error("union member should not be a scalar");
      return Change.deepMerge(acc, selection);
    }, {});
  const opNode = typeof op === "function" ? baseqlMetaData.entities.find((node) => node.type() === op()) : op;
  if (!opNode)
    throw new Error(`Cannot find node for return type ${getTypeString(typeof op === "function" ? op : op.type)}`);
  let obj = Object.fromEntries(
    Object.values(opNode.fields).map((field) => {
      const val = depth - 1 === 0 && !isGQLScalarType(field.type) || blacklist.includes(field.name) || childClass && field.fromParent ? void 0 : allFieldsSelectionSetObject(field.type, depth - 1, blacklist);
      return [field.name, val];
    }).filter((x) => !!x[1])
  );
  if (!childClass) {
    obj = baseqlMetaData.entities.reduce(
      (acc, node, d) => {
        if (d) {
          const selection = allFieldsSelectionSetObject(node, depth, blacklist, true);
          if (typeof selection === "number")
            throw new Error("child class should not be a scalar");
          return Change.deepMerge(acc, selection);
        }
        return acc;
      },
      obj,
      opNode
    );
  }
  if (opNode.fields._id)
    obj._id = 1;
  obj._class = 1;
  return obj;
};
var fragmentNameTaken = (name, selection, allFragments) => allFragments[name] && allFragments[name]?.selection !== selection;
var buildFragment = (op, selectionSet2, currentFragments, allFragments, parent = "", filterParent = false) => {
  const node = typeof op === "function" ? baseqlMetaData.entities.find((node2) => node2.type() === op()) : op;
  if (!node)
    throw new Error(`Cannot find node for return type ${getTypeString(typeof op === "function" ? op : op.type)}`);
  if (node.parent && filterParent)
    selectionSet2 = Object.fromEntries(
      Object.entries(selectionSet2).filter(([key]) => !Object.keys(node.parent.fields).includes(key))
    );
  const fields = Object.keys(selectionSet2);
  const typeName = getTypeString(node.type);
  const fragmentBaseName = `${typeName}Fragment`;
  const isInterface = !!node.children.length;
  let selection = Object.values(node.fields).filter((field) => fields.includes(field.name)).reduce((acc, field) => {
    const scalar = isGQLScalarType(field.type);
    const fieldSelector = selectionSet2[field.name];
    if (!scalar) {
      if (fieldSelector === 1) {
        return acc;
      }
      const nested = buildGQLSelectionSet(field.type, fieldSelector, {}, allFragments);
      if (!nested.selection)
        return acc;
      return `${acc} ${field.name} { ${nested.selection} }`;
    }
    return `${acc} ${field.name}`;
  }, "");
  if (!selection)
    return "";
  if (filterParent && parent)
    selection = ` ...${parent}Fragment${selection}`;
  let fragmentIndex = 0;
  let fragmentName;
  do {
    fragmentName = `${fragmentBaseName}${fragmentIndex !== 0 ? fragmentIndex : ""}`;
    fragmentIndex++;
  } while (fragmentNameTaken(fragmentName, selection, allFragments));
  currentFragments[fragmentName] = {
    baseName: fragmentBaseName,
    fragment: `fragment ${fragmentName} on ${isInterface ? `${typeName}Interface` : typeName} {${selection} }`,
    selection
  };
  if (!allFragments[fragmentName])
    allFragments[fragmentName] = currentFragments[fragmentName];
  return `...${fragmentName}`;
};
var buildGQLSelectionSet = (op, selectionSet2, currentFragments = {}, allFragments = {}) => {
  const type = typeof op === "function" ? op() : op.type();
  if (type instanceof BaseQLUnion)
    return buildUnionSelectionSet(type, selectionSet2, allFragments);
  const opNode = typeof op === "function" ? baseqlMetaData.entities.find((node) => node.type() === op()) : op;
  if (!opNode)
    throw new Error(`Cannot find node for return type ${getTypeString(typeof op === "function" ? op : op.type)}`);
  if (!("_id" in selectionSet2))
    selectionSet2._id = 1;
  if (!("_class" in selectionSet2))
    selectionSet2._class = 1;
  const reduced = baseqlMetaData.entities.reduce(
    (acc, node, d) => {
      const typeString = getTypeString(node.type);
      let parentNode = node;
      while (parentNode && !acc.hasFragment[getTypeString(parentNode.type)])
        parentNode = parentNode.parent;
      const fragment = buildFragment(
        node,
        selectionSet2,
        currentFragments,
        allFragments,
        parentNode ? getTypeString(parentNode.type) : "",
        d > 0
      );
      if (!fragment)
        return acc;
      acc.selection = `${acc.selection} ${fragment}`;
      acc.hasFragment[typeString] = true;
      return acc;
    },
    { selection: "", hasFragment: {} },
    opNode
  );
  return { selection: reduced.selection, fragments: allFragments };
};
var buildUnionSelectionSet = (union, selectionSet2, allFragments = {}) => {
  const definition = baseqlMetaData.unions[union._class];
  if (!definition)
    throw new Error(`Could not find unionDefinition to union with name ${union._class}`);
  const selections = definition.types().map((type) => buildGQLSelectionSet(() => type, selectionSet2, {}, allFragments));
  return { selection: selections.map((s) => s.selection).join(" "), fragments: allFragments };
};
var gqlSelectionSetToBqlSelection = (definition, fragments) => {
  if (definition.kind === Kind.DOCUMENT) {
    const operation = definition.definitions[0];
    if (!operation || operation.kind !== Kind.OPERATION_DEFINITION)
      return console.warn("not a operation definition!");
    return gqlSelectionSetToBqlSelection(operation.selectionSet, fragments);
  }
  if (definition.kind === Kind.OPERATION_DEFINITION) {
    return gqlSelectionSetToBqlSelection(definition.selectionSet, fragments);
  }
  const flatSelection = (selectionSet2) => selectionSet2.selections.flatMap((selection) => {
    if (selection.kind === Kind.FRAGMENT_SPREAD) {
      const fragment = fragments[selection.name.value];
      if (!fragment)
        throw new Error(`Fragment ${selection.name.value} is not defined in the operation request!`);
      return flatSelection(fragment.selectionSet);
    }
    if (selection.kind === Kind.INLINE_FRAGMENT)
      return flatSelection(selection.selectionSet);
    if (selection.kind === Kind.FIELD)
      return [
        [
          selection.name.value,
          selection.selectionSet ? gqlSelectionSetToBqlSelection(selection.selectionSet, fragments) : 1
        ]
      ];
  });
  return Object.fromEntries(flatSelection(definition));
};
export {
  allFieldsSelectionSetObject,
  buildGQLSelectionSet,
  buildUnionSelectionSet,
  generateOperationString,
  gqlSelectionSetToBqlSelection,
  narrow,
  selectionSet
};
